import { useHandler } from "@redotech/react-util/hook";
import {
  rcoItemQuantitySegmentSchema,
  RcoSegmentStep,
} from "@redotech/redo-model/advanced-flow/advanced-flow-db-parser";
import { SegmentNumberOperator } from "@redotech/redo-model/checkouts/checkout-tree";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { OperatorSelector } from "@redotech/redo-web/conversion/operator-selector";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";
import { PartialWithRequiredType } from "../..";

const segmentNumberOperatorToText: Record<SegmentNumberOperator, string> = {
  [SegmentNumberOperator.LESS_THAN]: "less than",
  [SegmentNumberOperator.LESS_THAN_OR_EQUAL]: "less than or equal to",
  [SegmentNumberOperator.EQUAL]: "equal to",
  [SegmentNumberOperator.GREATER_THAN]: "greater than",
  [SegmentNumberOperator.GREATER_THAN_OR_EQUAL]: "greater than or equal to",
};

export const ItemQuantityInspectorContent = memo(
  function ItemQuantityInspectorContent({ step }: { step: RcoSegmentStep }) {
    const segment = useMemo(
      () => rcoItemQuantitySegmentSchema.parse(step.segment),
      [step],
    );

    return (
      <>
        <Flex align="flex-start" dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            Condition
          </Text>
          <Text fontSize="xs" fontWeight="medium" textColor="primary">
            Cart item quantity is{" "}
            {segmentNumberOperatorToText[segment.operator]} {segment.quantity}
          </Text>
        </Flex>
        {segment.exclusions?.skus && segment.exclusions.skus.length > 0 && (
          <Flex align="flex-start" dir="column" gap="xs">
            <Text fontSize="xs" fontWeight="medium" textColor="secondary">
              Excluded SKUs
            </Text>
            <Flex dir="row" gap="md" wrap="wrap">
              {segment.exclusions.skus.map((sku, index) => (
                <RedoBadge color="gray" key={index} size="sm" text={sku} />
              ))}
            </Flex>
          </Flex>
        )}
      </>
    );
  },
);

export const EditableItemQuantityInspectorContent = memo(
  function EditableItemQuantityInspectorContent({
    step,
    setStep,
  }: {
    step: Omit<RcoSegmentStep, "matchingNextId" | "nonMatchingNextId"> & {
      matchingNextId?: string | undefined;
      nonMatchingNextId?: string | undefined;
    };
    setStep: (step: PartialWithRequiredType<RcoSegmentStep>) => void;
  }) {
    const segment = useMemo(
      () => rcoItemQuantitySegmentSchema.parse(step.segment),
      [step],
    );

    const handleOperatorSelected = useHandler(
      (operator: SegmentNumberOperator) => {
        const updatedSegment = { ...segment, operator };
        setStep({ ...step, segment: updatedSegment });
      },
    );

    const handleQuantityChange = useHandler((quantity: number) => {
      const updatedSegment = { ...segment, quantity };
      setStep({ ...step, segment: updatedSegment });
    });

    return (
      <>
        <Flex align="flex-start" dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            Condition
          </Text>
          <Text fontSize="xs" fontWeight="medium" textColor="primary">
            Cart item quantity is
          </Text>
        </Flex>
        <Flex>
          <OperatorSelector<SegmentNumberOperator>
            availableOptions={Object.values(SegmentNumberOperator)}
            onOperatorSelected={handleOperatorSelected}
            operatorToText={segmentNumberOperatorToText}
            selectedOperator={segment.operator}
          />
        </Flex>
        <RedoIncrementDecrement
          min={0}
          setValue={handleQuantityChange}
          size="small"
          step={1}
          value={segment.quantity}
        />
        {segment.exclusions?.skus && segment.exclusions.skus.length > 0 && (
          <Flex align="flex-start" dir="column" gap="xs">
            <Text fontSize="xs" fontWeight="medium" textColor="secondary">
              Excluded SKUs
            </Text>
            <Flex dir="row" gap="md" wrap="wrap">
              {segment.exclusions.skus.map((sku, index) => (
                <RedoBadge color="gray" key={index} size="sm" text={sku} />
              ))}
            </Flex>
          </Flex>
        )}
      </>
    );
  },
);
